import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "../../utils/axiosAuth";
import { Admin, Admins } from "src/@types/admins";
import { RootState } from "../store";
import useAuthToken from "../useAuthToken";

interface InitialState {
  admins?: Array<Admin>;
  total?: number;
  user: Admin | undefined;
  isLoading: boolean;
  msg: string | undefined;
}

export const getAdmins: any = createAsyncThunk<
  Admins,
  { page: number; perPage: number }
>("admins/getAdmins", async ({ page = 1, perPage = 10 }, { getState }) => {
  const token = (getState() as RootState).auth.accessToken;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await axios.get(
    `/adm?page=${page}&perPage=${perPage}`,
    config
  );
  return response.data;
});

export const getAdmin: any = createAsyncThunk<Admin, { id: string }>(
  "admins/getAdmin",
  async ({ id }, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await axios.get(`/adm/${id}`);
    return response.data;
  }
);

export const postNewAdmin: any = createAsyncThunk<
  {
    message: string;
    id: string;
  },
  {
    name: string;
    cnpj: string;
    email: string;
    phone: string;
    status: string;
    deleted: string;
    address: string;
  }
>(
  "admins/registerAdmin",
  async (
    { name, cnpj, email, phone, status, deleted, address },
    { getState }
  ) => {
    const token = (getState() as RootState).auth.accessToken;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await axios.post(
      "/adm/register",
      {
        name,
        cnpj,
        email,
        phone,
        status,
        deleted,
        address,
      },
      config
    );
    return response.data;
  }
);

export const putAdmin: any = createAsyncThunk<
  {
    message: string;
    id: string;
  },
  {
    id: string;
    name: string;
    email: string;
    birthday: Date;
    phone: string;
    picture: string;
    userType: string;
    recoveryHash: string;
    invitationHash: string;
  }
>(
  "admins/updateUser",
  async (
    {
      id,
      name,
      email,
      birthday,
      phone,
      picture,
      userType,
      recoveryHash,
      invitationHash,
    },
    { getState }
  ) => {
    const token = (getState() as RootState).auth.accessToken;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await axios.put(
      `/adm/${id}`,
      {
        name,
        email,
        birthday,
        phone,
        picture,
        userType,
        recoveryHash,
        invitationHash,
      },
      config
    );
    return response.data;
  }
);

export const blockAdmin: any = createAsyncThunk<
  {
    message: string;
    id: string;
  },
  {
    id: string;
    item: any;
  }
>("admins/blockAdmin", async ({ id, item }, { getState }) => {
  const token = (getState() as RootState).auth.accessToken;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await axios.put(`/adm/${id}`, { ...item }, config);
  return response.data;
});

export const deleteAdmin: any = createAsyncThunk<
  {
    message: string;
    id: string;
  },
  {
    id: string;
  }
>("admins/deleteAdmin", async ({ id }, { getState }) => {
  const token = (getState() as RootState).auth.accessToken;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await axios.delete(`/adm/${id}`, config);
  return response.data;
});

const initialState: InitialState = {
  admins: [],
  total: 0,
  user: undefined,
  isLoading: false,
  msg: "",
};

const adminsSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAdmins.pending, (state) => {
        state.isLoading = true;
        state.msg = undefined;
      })
      .addCase(getAdmins.fulfilled, (state, action) => {
        state.isLoading = false;
        state.admins = action.payload.data;
        state.total = action.payload.total;
        state.msg = undefined;
      })
      .addCase(getAdmins.rejected, (state, action) => {
        state.isLoading = false;
        state.msg = "Error ao fazer requisição";
      })
      .addCase(postNewAdmin.pending, (state) => {
        state.isLoading = true;
        state.msg = "";
      })
      .addCase(postNewAdmin.fulfilled, (state) => {
        state.isLoading = false;
        state.msg = "Administradora registrado!";
      })
      .addCase(postNewAdmin.rejected, (state) => {
        state.isLoading = false;
        state.msg = "Erro ao registrar Administradora!";
      })
      .addCase(putAdmin.pending, (state) => {
        state.isLoading = true;
        state.msg = "";
      })
      .addCase(putAdmin.fulfilled, (state) => {
        state.isLoading = false;
        state.msg = "Administradora atualizada!";
      })
      .addCase(putAdmin.rejected, (state) => {
        state.isLoading = false;
        state.msg = "Erro ao atualizar Administradora!";
      });
  },
});

// export const {  } = adminsSlice.actions;

export default adminsSlice.reducer;
